import { useMemo, useState } from "react";

import { TalentFilterSheet } from "@/components/filters/TalentFilterSheet";
import { getEligibilityFromBrowseFilter, syncBrowseFilterPlatforms } from "@/utils/browseFilter";
import { Heading } from "@/components/typography";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/input-group";
import { useFreshCities, useFreshCountries } from "../../../services/firebase";
import { contentTags } from "../../../shared/grailConstants";
import type { BrowseFilter, Currency, Platform } from "../../../shared/types";
import { eligibilityConstraintLabels, eligibilityHasConstraints } from "../../../utils/pitching/eligibility";

type EligibilitySectionProps = {
    eligibility: BrowseFilter;
    onChange: (value: BrowseFilter) => void;
    platforms: Platform[];
    currency?: Currency;
};

/** Eligibility summary and the filter sheet. Loads countries, cities, and tags for that sheet. */
export function EligibilitySection({
    eligibility,
    onChange,
    platforms,
    currency,
}: EligibilitySectionProps) {
    const [filtersOpen, setFiltersOpen] = useState(false);
    const countriesQuery = useFreshCountries();
    const countryList = countriesQuery.flatData
        .filter((country) => country.liveOnAirtable)
        .map((country) => country.label)
        .sort();
    const cityQuery = useFreshCities();
    const cityList = cityQuery.flatData.map((cityDoc) => cityDoc.city).sort();

    const persistedEligibility = useMemo(
        () => getEligibilityFromBrowseFilter(syncBrowseFilterPlatforms(eligibility, platforms)),
        [eligibility, platforms],
    );
    const pills = eligibilityConstraintLabels(persistedEligibility);
    const filled = eligibilityHasConstraints(persistedEligibility);

    return (
        <section id="eligibility" className="scroll-mt-16">
            <Heading type="h4" className="mb-4 font-bold">
                Creator Eligibility
            </Heading>
            <Card size="sm">
                <CardContent>
                    <Field>
                        <FieldLabel>Creator Eligibility Filters (Optional)</FieldLabel>
                        <InputGroup className={filled ? "h-auto min-h-9" : undefined}>
                            {filled ? (
                                // Chips wrap inside the field; Edit stays in the end slot.
                                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 py-1.5 pr-1.5 pl-2.5">
                                    {pills.map((pill) => (
                                        <Badge key={pill} variant="secondary">
                                            {pill}
                                        </Badge>
                                    ))}
                                </div>
                            ) : (
                                <InputGroupInput
                                    readOnly
                                    value="No eligibility requirements — any creator can be pitched"
                                />
                            )}
                            <InputGroupAddon align="inline-end" className="shrink-0 py-0">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => setFiltersOpen(true)}
                                >
                                    Edit
                                </Button>
                            </InputGroupAddon>
                        </InputGroup>
                        <FieldDescription>
                            Optional. Managers can only pitch creators who match these
                            requirements. Creators who don&apos;t match won&apos;t be
                            able to submit a pitch.
                        </FieldDescription>
                    </Field>
                </CardContent>
            </Card>

            <TalentFilterSheet
                open={filtersOpen}
                onOpenChange={setFiltersOpen}
                value={eligibility}
                onChange={onChange}
                platforms={platforms}
                showPriceChips={false}
                title="Eligibility requirements"
                description="Optional. Managers can only pitch creators who match these requirements. Creators who don't match won't be able to submit a pitch."
                currency={currency}
                countryList={countryList}
                tagList={contentTags}
                cityList={cityList}
            />
        </section>
    );
}
